'use client';

/**
 * 録音エンジン — 非表示でマウントされ、RecordButton の XState マシンと
 * セッションストアを橋渡しする。共有の開始・停止操作から制御される。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { RecordButton } from '@/components/transcribe/RecordButton';
import {
  consumeResumePending,
  getTranscriptionRecordingResumePhase,
  setTranscriptionAudioPrefs,
  setTranscriptionEngineControls,
  setTranscriptionRecordingSessionState,
  useTranscriptionRecordingSession,
} from '@/hooks/useTranscriptionRecordingSession';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { canRecordMixedAudio } from '@/lib/audioCapabilityDetector';

import type { AudioSource } from '@/hooks/useOpusRecorder';
import type { Speech2textInfo, TranscriptionPipelineState } from '@/hooks/useTranscriptionConnection';
import type { TranscriptionRecordingEngineControls, TranscriptionRecordingSessionState } from '@/hooks/useTranscriptionRecordingSession';

const AUDIO_SOURCE_PREFS_KEY = 'transcription.audioSourcePrefs.v2';
const AUDIO_LIST_HREF = '/planner/audio';
const AUDIO_LIST_LINK_LABEL = '音声一覧へ→';
const RECORDING_COMPLETE_TOAST_DURATION_MS = 10_000;

function openAudioListInNewTab(): void {
  if (typeof window === 'undefined') return;
  // Keep the destination consistent with FloatingRecorderFab.resolveDetailHref() fallback.
  window.open(AUDIO_LIST_HREF, '_blank', 'noopener,noreferrer');
}

function readInitialAudioPreferences(): { useTabAudio: boolean; useMicAudio: boolean } {
  try {
    const raw = window.localStorage.getItem(AUDIO_SOURCE_PREFS_KEY);
    const parsed = raw ? JSON.parse(raw) as { useTabAudio?: unknown; useMicAudio?: unknown } : null;
    return {
      useTabAudio: parsed?.useTabAudio === true,
      useMicAudio: getTranscriptionRecordingResumePhase() === 'recording'
        ? parsed?.useMicAudio !== false : true,
    };
  } catch { return { useTabAudio: false, useMicAudio: true }; }
}

export function TranscriptionEngine() {
  const session = useTranscriptionRecordingSession();
  const { canStartRecording } = useUserPermissions();
  const canRecord = canStartRecording;

  const audioSource = useMemo<AudioSource>(() => {
    if (session.useTabAudio && session.useMicAudio) return 'both';
    if (session.useTabAudio) return 'tab';
    return 'mic';
  }, [session.useTabAudio, session.useMicAudio]);

  // Initialize audio prefs (once).
  const [audioPreferencesReady, setAudioPreferencesReady] = useState(false);
  useEffect(() => {
    const supportsTabAudio = canRecordMixedAudio();
    const prefs = readInitialAudioPreferences();
    const useTabAudio = supportsTabAudio && prefs.useTabAudio;
    setTranscriptionAudioPrefs({ supportsTabAudio, useTabAudio, useMicAudio: prefs.useMicAudio });
    setAudioPreferencesReady(true);
  }, []);

  // Persist audio prefs.
  useEffect(() => {
    if (!audioPreferencesReady) return;
    try {
      window.localStorage.setItem(
        AUDIO_SOURCE_PREFS_KEY,
        JSON.stringify({ useTabAudio: session.useTabAudio, useMicAudio: session.useMicAudio }),
      );
    } catch { /* noop */ }
  }, [audioPreferencesReady, session.useTabAudio, session.useMicAudio]);

  const checkRecordingPermission = useCallback(() => {
    if (!canRecord) throw new Error('この機能を利用する権限がありません');
  }, [canRecord]);

  const handleControlsChange = useCallback((controls: Omit<TranscriptionRecordingEngineControls, 'checkPermission'> | null) => {
    setTranscriptionEngineControls(controls ? { ...controls, checkPermission: checkRecordingPermission } : null);
  }, [checkRecordingPermission]);

  const handleBeforeStart = useCallback(() => {
    checkRecordingPermission();
    return true;
  }, [checkRecordingPermission]);

  const callbacks = useMemo(() => ({
    onAudioCaptureStart: () => {
      setTranscriptionRecordingSessionState({ engineState: 'recording', recordingStartedAtMs: Date.now() });
    },
    onTranscriptUpdate: (transcript: string) => {
      if (transcript.trim()) setTranscriptionRecordingSessionState({ latestRealtimeTranscript: transcript });
    },
    onCommitStateUpdate: (s: { revision: number; committedText: string; draftText: string }) => {
      setTranscriptionRecordingSessionState({ rawRevision: s.revision, rawCommittedText: s.committedText, rawDraftText: s.draftText });
    },
    onFormattedTranscriptUpdate: (transcript: string) => {
      if (transcript.trim()) {
        setTranscriptionRecordingSessionState((prev) => ({
          formattedTranscript: transcript,
          activeTab: prev.formattedTranscript.trim() ? prev.activeTab : 'completed',
        }));
      }
    },
    onRecordingStart: () => {
      // meetingId は onConnected で既に設定されているため、ここでリセットしない
      // （リセットすると FAB の「詳細を開く」が遷移先 meetingId を失う）
      setTranscriptionRecordingSessionState({
        engineState: 'recording', isRecording: true, isProcessing: false, error: '',
        formattedTranscript: '', latestRealtimeTranscript: '',
        rawCommittedText: '', rawDraftText: '', rawRevision: null,
        pipelineState: null, speech2textInfo: null, statusMessage: null,
        activeTab: 'realtime', recordingStartedAtMs: Date.now(),
      });
    },
    onRecordingStop: () => {
      setTranscriptionRecordingSessionState({ isRecording: false, isProcessing: true });
    },
    onProcessingComplete: () => {
      setTranscriptionRecordingSessionState((prev) => ({
        isProcessing: false, activeTab: 'completed', completedCount: prev.completedCount + 1,
      }));
      toast('録音が完了しました', {
        description: '文字起こしを処理しています。',
        action: {
          label: AUDIO_LIST_LINK_LABEL,
          onClick: openAudioListInNewTab,
        },
        duration: RECORDING_COMPLETE_TOAST_DURATION_MS,
        closeButton: true,
      });
    },
    onConnected: (meetingId: string) => {
      setTranscriptionRecordingSessionState({ meetingId });
    },
    onFinalRequested: (meetingId: string) => {
      setTranscriptionRecordingSessionState({ meetingId, activeTab: 'completed' });
    },
    onError: (err: Error) => {
      setTranscriptionRecordingSessionState({ error: err.message, isRecording: false, isProcessing: false, engineState: 'error' });
    },
    onSpeech2textInfo: (info: Speech2textInfo) => {
      setTranscriptionRecordingSessionState({ speech2textInfo: info });
    },
    onPipelineStateUpdate: (state: TranscriptionPipelineState) => {
      setTranscriptionRecordingSessionState({ pipelineState: state });
    },
    onStatusMessage: (message: string | null) => {
      setTranscriptionRecordingSessionState({ statusMessage: message });
    },
    onStateValueChange: (value: string) => {
      if (value === 'idle' && consumeResumePending()) return; // skip initial idle during resume
      // 接続後の内部 recording は、音声取得・利用確認がまだ未完了。
      // onError publishes the error and stopped flags together before ownership ends.
      if (value === 'recording' || value === 'connected' || value === 'error') return;
      setTranscriptionRecordingSessionState({ engineState: value as TranscriptionRecordingSessionState['engineState'] });
    },
  }), []);

  const handleComplete = useCallback((transcript: string) => {
    setTranscriptionRecordingSessionState({
      latestRealtimeTranscript: transcript.trim() ? transcript : session.latestRealtimeTranscript,
      activeTab: 'completed', isProcessing: false,
    });
  }, [session.latestRealtimeTranscript]);

  return (
    <div className="hidden" aria-hidden="true">
      <RecordButton
        audioSource={audioSource}
        selectedTabStream={session.selectedTabStream}
        audioPreferencesReady={audioPreferencesReady}
        onControlsChange={handleControlsChange}
        onBeforeStart={handleBeforeStart}
        onAudioCaptureStart={callbacks.onAudioCaptureStart}
        onRecordingStart={callbacks.onRecordingStart}
        onRecordingStop={callbacks.onRecordingStop}
        onTranscriptUpdate={callbacks.onTranscriptUpdate}
        onCommitStateUpdate={callbacks.onCommitStateUpdate}
        onFormattedTranscriptUpdate={callbacks.onFormattedTranscriptUpdate}
        onProcessingComplete={callbacks.onProcessingComplete}
        onComplete={handleComplete}
        onConnected={callbacks.onConnected}
        onFinalRequested={callbacks.onFinalRequested}
        onError={callbacks.onError}
        onSpeech2textInfo={callbacks.onSpeech2textInfo}
        onPipelineStateUpdate={callbacks.onPipelineStateUpdate}
        onStatusMessage={callbacks.onStatusMessage}
        onStateValueChange={callbacks.onStateValueChange}
      />
    </div>
  );
}
